import React, { JSX, useEffect, useState } from 'react'
import { AnalyticsMetafield, CustomerTotals } from 'types/api'
import { cleanVariables, formatPrice, graphqlRequest, numberFormatter } from 'lib/helpers'
import { useQuery } from '@tanstack/react-query'
import { ExportModal, SortableHeader } from './SalesTable'
import { EmptyDataCard } from './EmptyDataCard'
import { RefreshingBlock } from './Loading'
import getPaginatedCustomers from '../graphql/getPaginatedCustomers.graphql'

interface CustomerTableProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const CustomerTable = ({ open, setOpen, token, client, filters }: CustomerTableProps): JSX.Element => {
  const PAGE_SIZE = 10
  const [page, setPage] = useState(0)
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' }>({ key: 'sales', direction: 'desc' })
  const idField = 'customerId'
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'customersPaginated', filters, client?.id, page, sortConfig],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate,
        pageSize: PAGE_SIZE,
        offset: page,
        sortDirection: sortConfig.direction,
        sort: sortConfig.key
      })
      const { customers } = await graphqlRequest<{ customers: CustomerTotals[] }>(
        getPaginatedCustomers, cleanVar, token
      )

      return customers ?? []
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  useEffect(() => {
    setPage(0)
  }, [sortConfig, filters])

  const canPrev = page > 1
  const canNext = true

  const handleSort = (key: string): void => {
    setSortConfig(prev =>
      prev?.key === key && prev.direction === 'asc'
        ? { key, direction: 'desc' }
        : { key, direction: 'asc' }
    )
  }

  if (!isFetching && ((data == null) || data.length === 0)) {
    return <EmptyDataCard message='No customer data available' />
  }

  const fillerCount = PAGE_SIZE - (data?.length ?? 0)

  return (
    <div data-testid='customer-table' className='relative mt-4 md:mx-0 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden'>
      {isFetching && RefreshingBlock}
      <ExportModal tableType='customer' open={open} setOpen={setOpen} data={data ?? []} />
      <div className='overflow-x-auto'>
        <table data-testid='sales-table' className='w-full text-left whitespace-nowrap min-w-[800px]'>
          <colgroup>
            <col className='w-full w-6/12' />
            <col className='lg:w-3/12' />
          </colgroup>
          <thead className='border-b border-lm-bg-accent dark:border-dm-bg-accent text-sm/6 '>
            <tr>
              <th
                scope='col'
                className='cursor-pointer pl-2 sm:pl-4 py-4 pr-6 text-sm font-semibold tracking-wide md:table-cell lg:pr-20 text-left'
              >
                <div className='inline-flex items-center gap-1 px-2 py-1.5 rounded-md justify-start'>
                  <span className='text-sm font-medium'>Email</span>
                </div>
              </th>
              <th
                scope='col'
                className='cursor-pointer sm:pl-4 py-4 pr-6 text-sm font-semibold tracking-wide md:table-cell lg:pr-20 text-left'
              >
                <div className='inline-flex items-center gap-1 px-2 py-1.5 rounded-md justify-start'>
                  <span className='text-sm font-medium'>First</span>
                </div>
              </th>
              <th
                scope='col'
                className='cursor-pointer sm:pl-4 py-4 pr-6 text-sm font-semibold tracking-wide md:table-cell lg:pr-20 text-left'
              >
                <div className='inline-flex items-center gap-1 px-2 py-1.5 rounded-md justify-start'>
                  <span className='text-sm font-medium'>Last</span>
                </div>
              </th>
              <th
                scope='col'
                className='cursor-pointer sm:pl-4 py-4 pr-6 text-sm  font-semibold tracking-wide md:table-cell lg:pr-20 text-left'
              >
                <div className='inline-flex items-center gap-1 px-2 py-1.5 rounded-md justify-start'>
                  <span className='text-sm font-medium'>SMS Marketing</span>
                </div>
              </th>
              <th
                scope='col'
                className='cursor-pointer sm:pl-4 py-4 pr-6 text-sm font-semibold tracking-wide md:table-cell lg:pr-20 text-left'
              >
                <div className='inline-flex items-center gap-1 px-2 py-1.5 rounded-md justify-start'>
                  <span className='text-sm font-medium'>Email Marketing</span>
                </div>
              </th>
              <SortableHeader
                label='Quantity'
                sortKey='quantity'
                sortConfig={sortConfig}
                onSort={handleSort}
              />

              <SortableHeader
                label='Sales'
                sortKey='sales'
                sortConfig={sortConfig}
                onSort={handleSort}
              />

            </tr>
          </thead>
          <tbody className='divide-y divide-lm-bg-secondary dark:divide-dm-bg-secondary'>
            {data?.map((item) => (
              <tr
                data-testid={`row-${item[idField]}`}
                key={item[idField]}
                onClick={() =>
                  setExpandedRow(expandedRow === item[idField] ? null : item[idField])}
                className='cursor-pointer md:cursor-default transition-none duration-200 hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
              >
                <td className='py-4 pr-4 pl-0 sm:pr-8'>
                  <div className={`text-sm/6 font-medium pl-4 sm:pl-6 transition-none duration-200 ${
    expandedRow === item[idField] ? 'whitespace-normal break-all' : 'overflow-hidden text-ellipsis'}  w-[33vw] max-w-[33vw] sm:w-auto sm:max-w-none`}
                  >
                    {item.email}
                  </div>
                </td>
                <td className='py-4 pr-6 pl-4 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                  {item.customerFirst}
                </td>
                <td className='py-4 pr-6 pl-4 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                  {item.customerLast}
                </td>
                <td className='py-4 pr-6 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                  {item.smsMarketingConsent}
                </td>
                <td className='py-4 pr-6 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                  {item.emailMarketingConsent}
                </td>
                <td className='py-4 pr-6 pl-4 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                  {numberFormatter(item.quantity)}
                </td>

                <td className='py-4 pr-6 sm:pl-6 sm:pr-8 text-sm/6 font-medium'>
                  {formatPrice(item.sales)}
                </td>

              </tr>
            ))}
            {Array.from({ length: fillerCount ?? 0 }).map((_, i) => (
              <tr key={`filler-${i}`}>
                <td className='py-4 px-4'>&nbsp;</td>
                <td className='py-4 px-4' />
                <td className='py-4 px-4' />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='flex items-center justify-between border-t border-lm-bg-accent dark:border-dm-bg-accent px-4 sm:px-6 py-3 text-sm'>
        <div>
          <span className='font-medium' />
          <span className='font-medium' />
          <span className='font-medium' />
        </div>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={!canPrev}
            className='px-3 py-1.5 rounded-lg border border-lm-text-secondary dark:border-dm-text-secondary disabled:opacity-40'
          >
            Prev
          </button>

          <button
            type='button'
            onClick={() => setPage(p => (p + 1))}
            disabled={!canNext}
            className='px-3 py-1.5 rounded-lg border border-lm-text-secondary dark:border-dm-text-secondary disabled:opacity-40'
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
